"use client"

import type React from "react"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import "./ActiveConnectors.css"

interface ActiveConnector {
  id: string
  name: string
  description: string
  lastModified: string
  totalRecords: number
  isActive: boolean
  fromPlatform: string
  toPlatform: string
  fromLogo: string
  toLogo: string
}

const ActiveConnectors: React.FC = () => {
  const navigate = useNavigate()
  const [connectors, setConnectors] = useState<ActiveConnector[]>([
    {
      id: "1",
      name: "HubSpot Contact ↔ Salesforce Lead",
      description:
        "Sync, manage, and add HubSpot Contacts as Salesforce Leads automatically to keep your sales data up-to-date across both platforms.",
      lastModified: "09/06/2025",
      totalRecords: 110,
      isActive: true,
      fromPlatform: "HubSpot",
      toPlatform: "Salesforce",
      fromLogo: "https://cdn.worldvectorlogo.com/logos/hubspot-1.svg",
      toLogo: "https://cdn.worldvectorlogo.com/logos/salesforce-2.svg",
    },
  ])

  const handleToggleConnector = (id: string) => {
    setConnectors((prev) =>
      prev.map((connector) => (connector.id === id ? { ...connector, isActive: !connector.isActive } : connector)),
    )
  }

  const handleEditConnector = (id: string) => {
    navigate(`/connector/setup?edit=${id}`)
  }

  const handleSettingsConnector = (id: string) => {
    navigate(`/connector/settings/${id}`)
  }

  const handleCancel = () => {
    navigate("/dashboard")
  }

  const handleContinue = () => {
    navigate("/connector/setup")
  }

  return (
    <div className="active-connectors">
      <div className="active-connectors-header">
        <h1>View Active Connections</h1>
        <p>View and manage all your active connectors in one place to monitor and control ongoing syncs.</p>
      </div>

      <div className="connectors-list">
        {connectors.map((connector) => (
          <div key={connector.id} className="connector-card">
            <div className="connector-main">
              <div className="connector-avatar">
                <div className="avatar-circle">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                </div>
              </div>

              <div className="connector-content">
                <div className="connector-title">
                  <h3>{connector.name}</h3>
                  <div className="connector-platforms">
                    <div className="platform-logos">
                      <img
                        src={connector.fromLogo || "/placeholder.svg"}
                        alt={connector.fromPlatform}
                        className="platform-logo"
                      />
                      <div className="sync-arrows">
                        <svg
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                          <polyline points="7.5,4.21 12,6.81 16.5,4.21" />
                          <polyline points="7.5,19.79 7.5,14.6 3,12" />
                          <polyline points="21,12 16.5,14.6 16.5,19.79" />
                        </svg>
                      </div>
                      <img
                        src={connector.toLogo || "/placeholder.svg"}
                        alt={connector.toPlatform}
                        className="platform-logo"
                      />
                    </div>
                  </div>
                </div>

                <div className="connector-description">
                  <p>{connector.description}</p>
                </div>

                <div className="connector-stats">
                  <div className="stat-item">
                    <span className="stat-label">Last Modified:</span>
                    <span className="stat-value">{connector.lastModified}</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-label">Total records synced:</span>
                    <span className="stat-value">{connector.totalRecords}</span>
                  </div>
                </div>
              </div>

              <div className="connector-controls">
                <div className="sync-status">
                  <span className="sync-status-text">Sync ON in production syncing</span>
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id={`toggle-${connector.id}`}
                      checked={connector.isActive}
                      onChange={() => handleToggleConnector(connector.id)}
                    />
                    <label htmlFor={`toggle-${connector.id}`} className="toggle-label">
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>

                <div className="connector-actions">
                  <button className="edit-button" onClick={() => handleEditConnector(connector.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                    </svg>
                  </button>
                  <button className="settings-button" onClick={() => handleSettingsConnector(connector.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="3" />
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="connectors-actions">
        <button className="cancel-button" onClick={handleCancel}>
          Cancel
        </button>
        <button className="continue-button" onClick={handleContinue}>
          Continue
        </button>
      </div>
    </div>
  )
}

export default ActiveConnectors
